import {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {STALE_TIME_30_SEC} from '@common/constants/time';
import {toVbaSexCode} from '@common/gender';
import {ArrowLeft, UserPlus, AlertCircle} from 'lucide-react';
import {PageHeader} from '@common-web/components/page-header';
import {StepIndicator} from '@common-web/components/ui/step-indicator';
import {Button} from '@common-web/components/ui/button';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {trpc, createMutationErrorHandler, formatTRPCError} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {useProductSelection} from '@client/hooks/useProductSelection';
import {STEPS, CHAMP_STEPS, INITIAL_MEMBER, INITIAL_ADDRESS} from './member-add/types';
import type {MemberInfo, AddressInfo, StepName} from './member-add/types';
import {MemberInfoStep} from './member-add/MemberInfoStep';
import {GroupCoverageStep} from './member-add/GroupCoverageStep';
import {AdditionalProductsStep} from './member-add/AdditionalProductsStep';
import {ReviewConfirmStep} from './member-add/ReviewConfirmStep';

export const MemberAdd = () => {
  const {groupId: urlGroupId} = useParams<{groupId: string}>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [member, setMember] = useState<MemberInfo>(INITIAL_MEMBER);
  const [address, setAddress] = useState<AddressInfo>(INITIAL_ADDRESS);

  const updateMember = <K extends keyof MemberInfo>(field: K, value: MemberInfo[K]) =>
    setMember((prev) => ({...prev, [field]: value}));
  const updateAddress = <K extends keyof AddressInfo>(field: K, value: AddressInfo[K]) =>
    setAddress((prev) => ({...prev, [field]: value}));

  // Group users with access to exactly one group shouldn't have to pick it —
  // seed the group selector with their only group (they can still change it).
  // vbaGroupId is null until the group is linked to VBA; no prefill in that case.
  const singleGroupProfile = useAuthStore((s) =>
    s.isGroupUser() && s.user?.groupProfiles?.length === 1 && s.user.groupProfiles[0].vbaGroupId
      ? s.user.groupProfiles[0]
      : null,
  );
  const [selectedGroupId, setSelectedGroupId] = useState(singleGroupProfile?.vbaGroupId ?? '');
  const [selectedGroupName, setSelectedGroupName] = useState(singleGroupProfile?.groupName ?? '');
  const [groupSearch, setGroupSearch] = useState('');
  const groupId = urlGroupId || selectedGroupId;
  const [coverageEffectiveDate, setCoverageEffectiveDate] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Champ groups still assign products at creation time; every other tenant
  // (Synrgy, Quantum, etc.) gets products through the analyzer flow after the
  // member is written to VBA. Detect Champ from the VBA group-ID prefix (CH),
  // mirroring the backend's resolveTenantConfigFromGroupId.
  const isChampGroup = groupId.toUpperCase().startsWith('CH');
  const steps = isChampGroup ? CHAMP_STEPS : STEPS;
  const stepName: StepName = steps[Math.min(currentStep, steps.length - 1)];

  const groupsQuery = trpc.group.vbaMembers.searchGroups.useQuery(
    {search: groupSearch},
    {enabled: !urlGroupId, staleTime: STALE_TIME_30_SEC, placeholderData: (prev) => prev},
  );

  const {
    additionalProductIds,
    allSelectedProductIds,
    getProductName,
    initialProductId,
    productCatalog,
    productsQuery,
    remainingProducts,
    setAdditionalProductIds,
    setInitialProductId,
    toggleAdditionalProduct,
  } = useProductSelection({groupId: isChampGroup ? groupId : undefined});

  const handleGroupChange = (nextGroupId: string, nextGroupName: string) => {
    setSelectedGroupId(nextGroupId);
    setSelectedGroupName(nextGroupName);
    // Products are group-scoped; clear any prior selection so a stale product
    // from a previously chosen group can't be submitted.
    setInitialProductId('');
    setAdditionalProductIds([]);
  };

  const createMutation = trpc.group.vbaMembers.create.useMutation({
    onError: (error) => {
      setErrorMessage(formatTRPCError(error));
      createMutationErrorHandler('Failed to create member')(error);
    },
  });

  const canProceedStep1 = member.firstName && member.lastName && member.ssn && member.dateOfBirth;
  const canProceedStep2 = groupId && coverageEffectiveDate && (!isChampGroup || initialProductId);

  const goBack = () => {
    if (urlGroupId) {
      navigate(`/groups/${urlGroupId}/members`);
    } else {
      navigate('/member-search');
    }
  };

  const handleCreate = () => {
    if (!groupId || !coverageEffectiveDate) return;
    if (isChampGroup && !initialProductId) return;
    setErrorMessage('');

    const hasAddress = address.street || address.city || address.state || address.zip;

    createMutation.mutate(
      {
        address: hasAddress
          ? {
              city: address.city || null,
              state: address.state || null,
              street: address.street || null,
              street2: address.street2 || null,
              zip: address.zip || null,
            }
          : undefined,
        coverageEffectiveDate,
        dateOfBirth: member.dateOfBirth,
        email: member.email || null,
        employeeId: member.employeeId || null,
        firstName: member.firstName,
        groupId,
        hireDate: member.hireDate || null,
        lastName: member.lastName,
        middleName: member.middleName || null,
        phone: member.phone || null,
        products: isChampGroup ? allSelectedProductIds : [],
        sexCode: member.sexCode ? toVbaSexCode(member.sexCode) : undefined,
        ssn: member.ssn,
      },
      {
        onSuccess: () => {
          setErrorMessage('');
          if (urlGroupId) {
            navigate(`/groups/${urlGroupId}/members`);
          } else {
            navigate('/member-search');
          }
        },
      },
    );
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader title='Add New Member' icon={<UserPlus className='h-6 w-6' />}>
          <Button
            className='bg-white hover:bg-gray-50 text-brand-navy border-2 border-white/20 font-medium transition-all shadow-sm hover:shadow-md'
            onClick={goBack}
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            {urlGroupId ? 'Back to Members' : 'Back to Search'}
          </Button>
        </PageHeader>

        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Content */}
        <div className='px-8 py-6 max-w-4xl mx-auto w-full'>
          {errorMessage && (
            <Alert variant='destructive' className='mb-6 border-red-200 bg-red-50'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {stepName === 'Member Info' && (
            <MemberInfoStep
              member={member}
              onUpdateMember={updateMember}
              address={address}
              onUpdateAddress={updateAddress}
              canProceed={!!canProceedStep1}
              onContinue={() => setCurrentStep(1)}
            />
          )}

          {stepName === 'Group & Coverage' && (
            <GroupCoverageStep
              urlGroupId={urlGroupId}
              selectedGroupId={selectedGroupId}
              selectedGroupName={selectedGroupName}
              onGroupChange={handleGroupChange}
              groupOptions={groupsQuery.data?.data ?? []}
              onGroupSearch={setGroupSearch}
              isLoadingGroups={groupsQuery.isFetching}
              coverageEffectiveDate={coverageEffectiveDate}
              onCoverageEffectiveDateChange={setCoverageEffectiveDate}
              showProductSelection={isChampGroup}
              initialProductId={initialProductId}
              onInitialProductChange={setInitialProductId}
              productCatalog={productCatalog}
              isLoadingProducts={productsQuery.isLoading}
              canProceed={!!canProceedStep2}
              onBack={() => setCurrentStep(0)}
              onContinue={() => setCurrentStep(2)}
            />
          )}

          {stepName === 'Additional Products' && (
            <AdditionalProductsStep
              remainingProducts={remainingProducts}
              additionalProductIds={additionalProductIds}
              onToggleAdditionalProduct={toggleAdditionalProduct}
              onSkip={() => {
                setAdditionalProductIds([]);
                setCurrentStep(3);
              }}
              onBack={() => setCurrentStep(1)}
              onContinue={() => setCurrentStep(3)}
            />
          )}

          {stepName === 'Review & Confirm' && (
            <ReviewConfirmStep
              member={member}
              address={address}
              groupId={groupId}
              coverageEffectiveDate={coverageEffectiveDate}
              stepNumber={steps.length}
              allSelectedProductIds={isChampGroup ? allSelectedProductIds : []}
              getProductName={getProductName}
              isCreating={createMutation.isPending}
              onBack={() => setCurrentStep(currentStep - 1)}
              onCreate={handleCreate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
